import { Button } from "components/DesignSystem/Button/Button";
import { Chip } from "components/DesignSystem/Chips/Chips";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { Search } from "components/DesignSystem/Search/Search";
import { ETD_FILTER_OPTIONS, MY_FILING_SORT_OPTIONS } from "constants/task";
import React from "react";
import SortFunnel from "../../../static/images/SortFunnel.svg";

type ConsultantDashBoardGlobalFilterProps = {
  searchedTerm: string;
  selectedStateFilter: string | null;
  tags: Array<{ uuid: string; title: string }>;
  selectedTag: string | null;
  taskStates: Array<{ uuid: string; name: string }>;
  templates: Array<{ uuid: string; title: string; year?: string }>;
  selectedTemplate: string | null;
  selectedSortOption: { id: number; title: string; value: string };
  setSelectedSortOption: (option: {
    id: number;
    title: string;
    value: string;
  }) => void;
  showSortFeature: boolean;
  update: (updateObj: { query: string; value: string | null }) => void;
  selectedETD: string | null;
};

export const ConsultantDashBoardGlobalFilter = ({
  searchedTerm,
  selectedStateFilter,
  tags,
  selectedTag,
  taskStates,
  templates,
  selectedTemplate,
  selectedSortOption,
  setSelectedSortOption,
  showSortFeature,
  update,
  selectedETD,
}: ConsultantDashBoardGlobalFilterProps) => {
  const selectedEtdLabel = ETD_FILTER_OPTIONS.find(
    ({ value }) => value === selectedETD
  )?.label;

  const handleSortChange = (value: string) => {
    const option = MY_FILING_SORT_OPTIONS.find(
      (sortOption) => sortOption.value === value
    );

    if (!option) return;

    setSelectedSortOption(option);
  };

  const searchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    update({ query: "search_term", value: value || null });
  };

  const templateFilter = (values: string[]) => {
    update({
      query: "task_template_filter_uuids",
      value: values.join(",") || null,
    });
  };

  const tagsFilter = (values: string[]) => {
    update({ query: "task_tag_filter_uuids", value: values.join(",") || null });
  };

  const handleEtdFilter = (value: string) => {
    update({
      query: "etd_filter",
      value: value || null,
    });
  };

  const stateFilter = (values: string[]) => {
    update({
      query: "task_state_filter_uuids",
      value: values.join(",") || null,
    });
  };

  return (
    <div className="t-flex t-w-full t-gap-2 t-items-center t-justify-start">
      <Search
        placeholder="Search..."
        defaultValue={searchedTerm || ""}
        onChange={searchQuery}
        autoFocus
        className="t-w-52"
      />

      {showSortFeature && (
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <Button size="xsmall" onClick={(e) => e.stopPropagation()}>
              <div className="t-flex t-items-center t-gap-1.5">
                <img src={SortFunnel} alt="icon" height="16px" width="16px" />
                <span className="t-text-sm t-text-text-70">Sort By:</span>
                <span className="t-text-sm t-text-text-90 t-font-medium">
                  {selectedSortOption.title}
                </span>
              </div>
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content
              sideOffset={4}
              align="start"
              side="bottom"
              className="t-px-2 t-py-3"
            >
              <Radio.Root
                value={selectedSortOption.value}
                onValueChange={handleSortChange}
                className="t-space-y-[5px]"
              >
                <Radio.Content className="t-flex t-flex-col t-gap-y-1.5 t-py-1.5 t-px-3">
                  {MY_FILING_SORT_OPTIONS.map((option) => (
                    <Radio.Item
                      key={option.id}
                      value={option.value}
                      className="t-text-text-100 t-text-body-sm t-flex t-items-center t-gap-x-2"
                    >
                      {option.title}
                    </Radio.Item>
                  ))}
                </Radio.Content>
              </Radio.Root>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      )}
      <Filter.Root
        defaultValue="STATE"
        title="Filter"
        capsule={
          <>
            {selectedStateFilter &&
              selectedStateFilter?.split(",").length > 0 && (
                <Chip
                  onRemove={() => stateFilter([])}
                  isActive
                  filterType="STATE"
                >
                  State ({selectedStateFilter.split(",").length})
                </Chip>
              )}
            {selectedTemplate && selectedTemplate?.split(",").length > 0 && (
              <Chip
                onRemove={() => templateFilter([])}
                isActive
                filterType="FILING"
              >
                Filing ({selectedTemplate.split(",").length})
              </Chip>
            )}
            {selectedTag && selectedTag?.split(",").length > 0 && (
              <Chip onRemove={() => tagsFilter([])} isActive filterType="TAG">
                Tag ({selectedTag.split(",").length})
              </Chip>
            )}
            {selectedETD && selectedEtdLabel && (
              <Chip
                onRemove={() => handleEtdFilter("")}
                isActive
                filterType="ETD"
              >
                {selectedEtdLabel}
              </Chip>
            )}
          </>
        }
      >
        <Filter.Portal>
          <Filter.List>
            <Filter.ListItem value="STATE">State</Filter.ListItem>
            <Filter.ListItem value="FILING">Filing</Filter.ListItem>
            <Filter.ListItem value="TAG">Tag</Filter.ListItem>
            <Filter.ListItem value="ETD">ETD</Filter.ListItem>
          </Filter.List>
          <Filter.Body value="STATE" block>
            {taskStates && (
              <MultiSelectFilter
                totalCount={taskStates.length}
                onChange={stateFilter}
                options={taskStates.map((taskState) => ({
                  value: taskState.uuid,
                  label: taskState.name,
                }))}
                selected={selectedStateFilter?.split(",")}
              />
            )}
          </Filter.Body>
          <Filter.Body value="FILING" block>
            <MultiSelectFilter
              onChange={templateFilter}
              options={templates?.map((template) => ({
                value: template.uuid,
                label: `${template.title}  ${
                  !template.year ? "" : `- ${template.year}`
                }`,
              }))}
              selected={selectedTemplate?.split(",")}
            />
          </Filter.Body>
          <Filter.Body value="TAG" block>
            <MultiSelectFilter
              onChange={tagsFilter}
              options={tags?.map((tag) => ({
                value: tag.uuid,
                label: tag.title,
              }))}
              selected={selectedTag?.split(",")}
            />
          </Filter.Body>
          <Filter.Body value="ETD" block>
            <Radio.Root
              value={selectedETD || ""}
              onValueChange={handleEtdFilter}
            >
              <Radio.Content className="t-gap-3 t-flex t-flex-col">
                {ETD_FILTER_OPTIONS.map((item) => (
                  <Radio.Item key={item.value} value={item.value}>
                    {item.label}
                  </Radio.Item>
                ))}
              </Radio.Content>
            </Radio.Root>
          </Filter.Body>
        </Filter.Portal>
      </Filter.Root>
    </div>
  );
};
